/**
 * @module exploreService
 * API service for the Explore feature — curated talent browsing and AI assistant.
 * @userstory US-EXP-01 — Explore feed overview
 * @userstory US-EXP-05 — Section expand with filters
 * @userstory US-EXP-13 — AI assistant session creation
 * @userstory US-EXP-14 — AI assistant streaming messages
 * @userstory US-EXP-15 — AI assistant job description attachment
 * @userstory US-EXP-16 — AI assistant usage tracking
 */
import apiClient from './apiClient';
import authService from './authService';

// ============================================================================
// Type Definitions — Explore Feed
// ============================================================================

export interface ExploreFeedResponseDto {
	forYou: ExploreSectionDto;
	newOnPlatform: ExploreSectionDto;
	matchingProjects: ExploreSectionDto | null;
}

export interface ExploreSectionDto {
	sectionName: string;
	totalCount: number;
	items: ExploreTalentCardDto[];
}

export interface ExploreTalentCardDto {
	talentUserId: string;
	fullName: string;
	title: string | null;
	location: string | null;
	skills: string[];
	hourlyRate: number | null;
	availableFrom: string | null;
	photoUrl: string | null;
	affinityScore: number | null;
	matchExplanation: string | null;
	isNew: boolean;
	allianceIndicator: string | null;
	isBookmarked: boolean;
	collectionCount: number;
	matchingProjectName: string | null;
	personalStatement: string | null;
	yearsExperience: number | null;
	isImmediatelyAvailable: boolean;
}

export interface ExploreSectionExpandRequest {
	section: string;
	availableFrom?: string;
	hourlyRateMin?: number;
	hourlyRateMax?: number;
	skills?: string[];
	location?: string;
	sortBy?: string;
	page?: number;
	pageSize?: number;
}

export interface ExploreSectionExpandResponseDto {
	sectionName: string;
	totalCount: number;
	page: number;
	pageSize: number;
	items: ExploreTalentCardDto[];
}

// ============================================================================
// Type Definitions — AI Assistant
// ============================================================================

export interface CreateExploreAiSessionRequest {
	talentUserIds: string[];
}

export interface ExploreAiSessionDto {
	sessionId: string;
	title: string | null;
	profileCount: number;
	createdAt: string;
	lastInteractionAt: string;
	messageCount: number;
}

export interface ExploreAiMessageRequest {
	content: string;
	replayFromIndex?: number;
}

export interface ExploreAiSessionDetailDto {
	sessionId: string;
	title: string | null;
	profileCount: number;
	createdAt: string;
	lastInteractionAt: string;
	talentUserIds: string[];
	messages: ExploreAiMessageDto[];
}

export interface ExploreAiMessageDto {
	id: string;
	role: string;
	content: string;
	messageIndex: number;
	createdAt: string;
}

export interface ExploreAiStreamChunk {
	type: 'status' | 'text_delta' | 'done' | 'error';
	content?: string;
	errorMessage?: string;
}

export interface ExploreAiUsageDto {
	usedMessages: number;
	maxMessages: number | null;
	remainingMessages: number | null;
	resetDate: string | null;
}

// ============================================================================
// API Service
// ============================================================================

const exploreService = {
	// ========================================================================
	// Explore Feed
	// ========================================================================

	/** @userstory US-EXP-01 */
	getFeed: async (organizationId: string): Promise<ExploreFeedResponseDto> => {
		const response = await apiClient.get<ExploreFeedResponseDto>(
			`/api/organizations/${organizationId}/explore/feed`,
		);
		return response.data;
	},

	/** @userstory US-EXP-05 */
	getSection: async (
		organizationId: string,
		request: ExploreSectionExpandRequest,
	): Promise<ExploreSectionExpandResponseDto> => {
		const response = await apiClient.get<ExploreSectionExpandResponseDto>(
			`/api/organizations/${organizationId}/explore/sections`,
			{ params: request },
		);
		return response.data;
	},

	// ========================================================================
	// AI Assistant
	// ========================================================================

	/** @userstory US-EXP-13 */
	createAiSession: async (
		organizationId: string,
		request: CreateExploreAiSessionRequest,
	): Promise<ExploreAiSessionDto> => {
		const response = await apiClient.post<ExploreAiSessionDto>(
			`/api/organizations/${organizationId}/explore/ai-assistant/sessions`,
			request,
		);
		return response.data;
	},

	/**
	 * Send a message to the AI assistant and receive streaming response via SSE.
	 * Returns an abort function to cancel the stream.
	 * @userstory US-EXP-14, US-EXP-25, US-EXP-26
	 */
	sendAiMessageStream: (
		organizationId: string,
		sessionId: string,
		content: string,
		onChunk: (chunk: ExploreAiStreamChunk) => void,
		onError: (error: string) => void,
		replayFromIndex?: number,
	): (() => void) => {
		const controller = new AbortController();
		const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
		const token = authService.getAccessToken();

		const body: ExploreAiMessageRequest = { content };
		if (replayFromIndex !== undefined) {
			body.replayFromIndex = replayFromIndex;
		}

		fetch(
			`${API_BASE_URL}/api/organizations/${organizationId}/explore/ai-assistant/sessions/${sessionId}/messages/stream`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				body: JSON.stringify(body),
				signal: controller.signal,
			},
		)
			.then(async (response) => {
				if (!response.ok) {
					throw new Error(`HTTP ${response.status}: ${response.statusText}`);
				}

				const reader = response.body?.getReader();
				if (!reader) {
					throw new Error('Response body is not readable');
				}

				const decoder = new TextDecoder();
				let buffer = '';

				// eslint-disable-next-line no-constant-condition
				while (true) {
					const { done, value } = await reader.read();
					if (done) break;

					buffer += decoder.decode(value, { stream: true });

					const lines = buffer.split('\n\n');
					buffer = lines.pop() || '';

					for (const line of lines) {
						if (line.startsWith('data: ')) {
							try {
								const data: ExploreAiStreamChunk = JSON.parse(line.slice(6));
								onChunk(data);
							} catch (parseError) {
								console.error('Failed to parse SSE data:', parseError);
							}
						}
					}
				}
			})
			.catch((err) => {
				if (err.name !== 'AbortError') {
					onError(err.message || 'Stream connection failed');
				}
			});

		return () => controller.abort();
	},

	/** @userstory US-EXP-16 */
	getAiUsage: async (organizationId: string): Promise<ExploreAiUsageDto> => {
		const response = await apiClient.get<ExploreAiUsageDto>(
			`/api/organizations/${organizationId}/explore/ai-assistant/usage`,
		);
		return response.data;
	},

	/** @userstory US-EXP-23 — List all active sessions */
	getAiSessions: async (organizationId: string): Promise<ExploreAiSessionDto[]> => {
		const response = await apiClient.get<ExploreAiSessionDto[]>(
			`/api/organizations/${organizationId}/explore/ai-assistant/sessions`,
		);
		return response.data;
	},

	/** @userstory US-EXP-23 — Get session detail with messages */
	getAiSessionById: async (
		organizationId: string,
		sessionId: string,
	): Promise<ExploreAiSessionDetailDto> => {
		const response = await apiClient.get<ExploreAiSessionDetailDto>(
			`/api/organizations/${organizationId}/explore/ai-assistant/sessions/${sessionId}`,
		);
		return response.data;
	},

	/** @userstory US-EXP-23 — Soft-delete a session */
	deleteAiSession: async (organizationId: string, sessionId: string): Promise<void> => {
		await apiClient.delete(
			`/api/organizations/${organizationId}/explore/ai-assistant/sessions/${sessionId}`,
		);
	},
};

export default exploreService;
